/**
 * AdminMasterFeaturesPage (Iter 9.39e)
 *
 * Kanban + table for db.master_features:
 *   Stage columns: Future / Building / Testing / Live
 *   Per row: name, description, stage, tier_visibility[], shows_on[],
 *            user_pain_point, success_metric, tracked_events[], test_accounts[]
 *
 * Visibility rules enforced by /api/me/visible-features:
 *   - live      → all users (filtered by tier_visibility if set)
 *   - testing   → only listed test_accounts + super_admin
 *   - building  → only super_admin + test_accounts
 *   - future    → hidden from everyone except super_admin
 */
import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Layers, Plus, Save, Loader2, Trash2, Activity, Eye, Users } from "lucide-react";
import { toast } from "sonner";
import MultiSelectChips from "@/components/admin/MultiSelectChips";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const STAGES = [
  { value: "future",   label: "Future",   note: "Hidden from everyone except admin." },
  { value: "building", label: "Building", note: "Visible to super-admins + test accounts only." },
  { value: "testing",  label: "Testing",  note: "Visible to listed test accounts." },
  { value: "live",     label: "Live",     note: "Visible everywhere (tier-gated)." },
];
const TIERS  = ["free", "solo", "team", "luxe", "admin"];
const SHOWS_ON_OPTIONS = ["/me", "host_dashboard", "admin", "public_landing", "envelope", "rsvp", "confirmation"];

export default function AdminMasterFeaturesPage() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/master-features`, { headers: auth() });
      if (r.ok) { const d = await r.json(); setFeatures(d.features || []); }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const r = await fetch(`${API}/admin/master-features`, {
      method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify({ name: "New feature", stage: "future" }),
    });
    if (r.ok) { const d = await r.json(); setEditingId(d.feature.feature_id); load(); }
  };

  const setStage = async (fid, stage) => {
    await fetch(`${API}/admin/master-features/${fid}`, {
      method: "PUT", headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify({ stage }),
    });
    toast.success(`Moved to ${stage}`);
    load();
  };

  const remove = async (fid) => {
    if (!window.confirm("Delete?")) return;
    await fetch(`${API}/admin/master-features/${fid}`, { method: "DELETE", headers: auth() });
    load();
  };

  if (loading) return <div className="p-10"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div>;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="admin-master-features-page">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Layers className="w-5 h-5" />
        <h1 className="text-xl font-bold">Master Features</h1>
        <Badge className="bg-purple-100 text-purple-700 border-0">{features.length}</Badge>
        <div className="flex-1" />
        <Button size="sm" onClick={create} className="bg-purple-600 hover:bg-purple-700" data-testid="features-add">
          <Plus className="w-3.5 h-3.5 mr-1" />Add feature
        </Button>
      </div>
      <p className="text-xs text-gray-500 mb-4">Pipeline of every product feature. Drag stage to change visibility. Future = hidden, Building = admin+test, Testing = listed test accounts, Live = everyone (tier gated).</p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="features-kanban">
        {STAGES.map((s) => {
          const col = features.filter((f) => (f.stage || "future") === s.value);
          return (
            <div key={s.value} className="bg-gray-50 rounded-xl">
              <div className="px-3 py-2 border-b border-black/5 flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wider">{s.label}</span>
                <Badge variant="outline" className="bg-white text-[10px]">{col.length}</Badge>
              </div>
              <div className="p-2 space-y-2 max-h-[640px] overflow-y-auto">
                {col.map((f) => (
                  <FeatureCard
                    key={f.feature_id}
                    feature={f}
                    isEditing={editingId === f.feature_id}
                    onEdit={() => setEditingId(f.feature_id)}
                    onClose={() => { setEditingId(null); load(); }}
                    onSetStage={setStage}
                    onDelete={remove}
                  />
                ))}
                {col.length === 0 && <div className="text-[11px] text-gray-400 px-2 py-4 text-center">{s.note}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FeatureCard({ feature, isEditing, onEdit, onClose, onSetStage, onDelete }) {
  const [draft, setDraft] = useState(feature);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(feature), [feature]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/master-features/${feature.feature_id}`, {
        method: "PUT", headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      if (r.ok) { toast.success("Saved"); onClose(); }
    } finally { setSaving(false); }
  };

  if (!isEditing) {
    return (
      <Card className="border" data-testid={`feature-card-${feature.feature_id}`}>
        <CardContent className="p-3 space-y-1.5">
          <div className="flex items-start gap-1.5">
            <button type="button" onClick={onEdit} className="text-sm font-medium text-left flex-1 hover:underline" data-testid={`feature-edit-${feature.feature_id}`}>
              {feature.name}
            </button>
            <button onClick={() => onDelete(feature.feature_id)} className="text-gray-300 hover:text-red-500" data-testid={`feature-delete-${feature.feature_id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          {feature.description && <p className="text-[11px] text-gray-500 line-clamp-2">{feature.description}</p>}
          <div className="flex flex-wrap gap-1">
            {(feature.tier_visibility || []).slice(0, 4).map((t) => <Badge key={t} variant="outline" className="text-[9px]">{t}</Badge>)}
            {(feature.shows_on || []).slice(0, 3).map((s) => <Badge key={s} className="bg-purple-50 text-purple-700 border-0 text-[9px]">{s}</Badge>)}
          </div>
          <div className="flex items-center gap-1 pt-1 flex-wrap">
            {STAGES.filter((s) => s.value !== feature.stage).map((s) => (
              <button key={s.value} onClick={() => onSetStage(feature.feature_id, s.value)} className="text-[10px] text-gray-500 hover:text-purple-700 underline" data-testid={`feature-move-${feature.feature_id}-${s.value}`}>
                → {s.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-purple-200 ring-2 ring-purple-100" data-testid={`feature-edit-card-${feature.feature_id}`}>
      <CardContent className="p-3 space-y-2">
        <div><Label className="text-[10px] text-gray-500">Name</Label><Input className="text-sm" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid={`feature-input-name-${feature.feature_id}`} /></div>
        <div><Label className="text-[10px] text-gray-500">Description</Label>
          <textarea className="w-full text-xs border rounded p-1.5" rows={2} value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid={`feature-input-desc-${feature.feature_id}`} />
        </div>
        <div><Label className="text-[10px] text-gray-500"><Activity className="w-3 h-3 inline mr-0.5" />User pain point</Label>
          <Input className="text-xs" value={draft.user_pain_point || ""} onChange={(e) => setDraft({ ...draft, user_pain_point: e.target.value })} data-testid={`feature-input-pain-${feature.feature_id}`} placeholder="What problem does this solve?" />
        </div>
        <div><Label className="text-[10px] text-gray-500">Success metric</Label>
          <Input className="text-xs" value={draft.success_metric || ""} onChange={(e) => setDraft({ ...draft, success_metric: e.target.value })} data-testid={`feature-input-metric-${feature.feature_id}`} placeholder="e.g. >70% activation in 30 days" />
        </div>
        <div><Label className="text-[10px] text-gray-500"><Eye className="w-3 h-3 inline mr-0.5" />Shows on</Label>
          <MultiSelectChips value={draft.shows_on || []} onChange={(v) => setDraft({ ...draft, shows_on: v })} options={SHOWS_ON_OPTIONS} testid={`feature-shows-on-${feature.feature_id}`} />
        </div>
        <div><Label className="text-[10px] text-gray-500"><Users className="w-3 h-3 inline mr-0.5" />Tier visibility (live stage only)</Label>
          <MultiSelectChips value={draft.tier_visibility || []} onChange={(v) => setDraft({ ...draft, tier_visibility: v })} options={TIERS} testid={`feature-tiers-${feature.feature_id}`} />
        </div>
        <div><Label className="text-[10px] text-gray-500">Tracked analytics events</Label>
          <MultiSelectChips value={draft.tracked_events || []} onChange={(v) => setDraft({ ...draft, tracked_events: v })} testid={`feature-tracked-${feature.feature_id}`} />
        </div>
        <div><Label className="text-[10px] text-gray-500">Test account user_ids (testing/building stage)</Label>
          <MultiSelectChips value={draft.test_accounts || []} onChange={(v) => setDraft({ ...draft, test_accounts: v })} testid={`feature-test-${feature.feature_id}`} placeholder="usr_..." />
        </div>
        <div className="flex items-center gap-1.5 pt-1">
          <Button size="sm" className="h-7 bg-purple-600" onClick={save} disabled={saving} data-testid={`feature-save-${feature.feature_id}`}><Save className="w-3 h-3 mr-1" />{saving ? "Saving…" : "Save"}</Button>
          <Button size="sm" variant="outline" className="h-7" onClick={onClose} data-testid={`feature-cancel-${feature.feature_id}`}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}
